import { AppBadge, AppSelect } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import type { SalesforceProjectMember } from 'backend/types'
import { useMemo } from 'react'
import { COLOR_CATEGORY, getColor } from '@/lib/color-registry'
import { useSalesforceProjectMembers } from '@/shared-hooks/useSalesforceProjectMembers'

interface MemberWithRoles {
  memberId: number
  memberName: string
  employeeNumber: number | null
  roles: {
    typeId: number
    shortName: string
  }[]
}

/**
 * メンバーIDでグループ化し、複数のロールをroles配列にする
 */
function groupMembersByIdWithRoles(members: SalesforceProjectMember[]): MemberWithRoles[] {
  const memberMap = new Map<number, MemberWithRoles>()

  for (const member of members) {
    const existing = memberMap.get(member.memberId)
    if (existing) {
      // 同じロールが重複しないようにチェック
      if (!existing.roles.some((r) => r.typeId === member.salesforceProjectMemberTypeId)) {
        existing.roles.push({
          typeId: member.salesforceProjectMemberTypeId,
          shortName: member.salesforceProjectMemberTypeShortName,
        })
      }
    } else {
      memberMap.set(member.memberId, {
        memberId: member.memberId,
        memberName: member.memberName,
        employeeNumber: member.employeeNumber,
        roles: [
          {
            typeId: member.salesforceProjectMemberTypeId,
            shortName: member.salesforceProjectMemberTypeShortName,
          },
        ],
      })
    }
  }

  return Array.from(memberMap.values())
}

interface TaskMemberSelectProps {
  value: number | null
  onChange: (memberId: number | null) => void
  salesforceProjectId: number | null
  label?: string
  required?: boolean
  disabled?: boolean
  placeholder?: string
  error?: string
  id?: string
}

/**
 * タスク担当者選択コンポーネント
 */
export function TaskMemberSelect({
  value,
  onChange,
  salesforceProjectId,
  label = '担当者',
  required = false,
  disabled = false,
  placeholder,
  error,
  id,
}: TaskMemberSelectProps) {
  // メンバー一覧取得
  const { data: membersData, isLoading, error: fetchError } = useSalesforceProjectMembers(salesforceProjectId)

  const members = useMemo(() => groupMembersByIdWithRoles(membersData?.members ?? []), [membersData?.members])

  // 404エラー（プロジェクトが見つからない）の場合は情報メッセージとして表示
  const is404 = fetchError?.message.includes('not found') || fetchError?.message.includes('404')
  const fetchErrorMessage = fetchError
    ? is404
      ? 'Salesforceプロジェクトが見つかりません'
      : `メンバーの取得に失敗しました: ${fetchError.message}`
    : undefined

  const handleValueChange = (val: string) => {
    onChange(val ? Number(val) : null)
  }

  const getPlaceholder = () => {
    if (placeholder) return placeholder
    if (isLoading) return '読み込み中...'
    if (!salesforceProjectId) return '案件が未設定です'
    return '担当者を選択'
  }

  return (
    <AppSelect
      id={id}
      label={label}
      required={required}
      value={value?.toString() ?? ''}
      onValueChange={handleValueChange}
      disabled={disabled || isLoading || !salesforceProjectId}
      placeholder={getPlaceholder()}
      options={members.map((member) => ({
        value: member.memberId.toString(),
        label: member.memberName,
        leftItem: (
          <AppBadge color='secondary' outline className='font-mono'>
            {member.employeeNumber ?? '不明'}
          </AppBadge>
        ),
        rightItem: (
          <>
            {member.roles.map((role) => (
              <AppBadge
                key={role.typeId}
                color={getColor(COLOR_CATEGORY.ROLE, role.typeId)}
                className='text-xs w-12.5 text-center inline-block'
              >
                {role.shortName}
              </AppBadge>
            ))}
          </>
        ),
      }))}
      error={error || fetchErrorMessage}
    />
  )
}
